import React from "react";

interface SafeBoundaryProps {
  children: React.ReactNode;
}

interface SafeBoundaryState {
  hasError: boolean;
  error?: Error | null;
}

export class SafeBoundary extends React.Component<SafeBoundaryProps, SafeBoundaryState> {
  constructor(props: SafeBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error } as SafeBoundaryState;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log for debugging
    // eslint-disable-next-line no-console
    console.error("UI crashed:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    // Soft reload to recover from persistent state errors
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-lg w-full border rounded-lg bg-card text-card-foreground p-6 shadow">
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground mt-2">
              The page crashed. We've logged it to the console.
            </p>
            {this.state.error?.message && (
              <pre className="mt-4 p-3 rounded bg-muted/50 text-xs overflow-auto max-h-40">
                {this.state.error.message}
              </pre>
            )}
            <button onClick={this.handleReset} className="mt-4 inline-flex px-4 py-2 rounded bg-primary text-primary-foreground">
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children as React.ReactElement;
  }
}

export default SafeBoundary;
